import { useAppLanguage } from '@/hooks/useAppLanguage';
import Colors from '@/constants/colors';
import DetailPageLayout from '@/components/DetailPageLayout';
import DetailPageHeader from '@/components/DetailPageHeader';

const content = {
  en: {
    title: 'About Umland',
    mission: {
      title: 'Our Mission',
      text: 'Umland is a free guide for expats, newcomers, and anyone interested in life in Iceland. Our goal is to make your adaptation easier by providing useful information, practical tips, and local insights all in one place.',
    },
    features: {
      title: 'What We Offer',
      items: [
        'Articles with practical advice on living in Iceland',
        'Lifehacks and tips from expat community',
        'Information about local events and activities',
        'Useful places: services, shops, organizations',
        'Home screen widgets: weather, currency rates, Icelandic holidays, word of the day',
      ],
    },
    audience: {
      title: 'Who Is It For',
      text: 'Whether you just moved to Iceland, planning to relocate, or simply curious about life on this unique island, Umland is here to help. The app is designed with expats in mind but welcomes everyone.',
    },
    languages: {
      title: 'Languages',
      text: 'The app is available in English, Russian, and Polish. We plan to add more languages in the future.',
    },
    contact: {
      title: 'Contact Us',
      text: 'Have suggestions, found an error, or want to contribute? We would love to hear from you!',
      email: 'support@umland.is',
    },
  },
  ru: {
    title: 'Об Umland',
    mission: {
      title: 'Наша миссия',
      text: 'Umland - это бесплатный гид для экспатов, новоприбывших и всех, кто интересуется жизнью в Исландии. Наша цель - упростить вашу адаптацию, предоставляя полезную информацию, практические советы и местные инсайты в одном месте.',
    },
    features: {
      title: 'Что мы предлагаем',
      items: [
        'Статьи с практическими советами о жизни в Исландии',
        'Лайфхаки и советы от сообщества экспатов',
        'Информация о местных событиях и мероприятиях',
        'Полезные места: сервисы, магазины, организации',
        'Виджеты на главном экране: погода, курсы валют, исландские праздники, слово дня',
      ],
    },
    audience: {
      title: 'Для кого это',
      text: 'Если вы только переехали в Исландию, планируете переезд или просто интересуетесь жизнью на этом уникальном острове - Umland здесь, чтобы помочь. Приложение создано с учетом потребностей экспатов, но открыто для всех.',
    },
    languages: {
      title: 'Языки',
      text: 'Приложение доступно на английском, русском и польском языках. В будущем мы планируем добавить больше языков.',
    },
    contact: {
      title: 'Связаться с нами',
      text: 'Есть предложения, нашли ошибку или хотите внести вклад? Мы будем рады вашему отзыву!',
      email: 'support@umland.is',
    },
  },
  pl: {
    title: 'O Umland',
    mission: {
      title: 'Nasza misja',
      text: 'Umland to bezpłatny przewodnik dla ekspatów, nowo przybyłych i wszystkich zainteresowanych życiem na Islandii. Naszym celem jest ułatwienie adaptacji poprzez dostarczanie przydatnych informacji, praktycznych wskazówek i lokalnych spostrzeżeń w jednym miejscu.',
    },
    features: {
      title: 'Co oferujemy',
      items: [
        'Artykuły z praktycznymi poradami dotyczącymi życia na Islandii',
        'Lifehacki i wskazówki od społeczności ekspatów',
        'Informacje o lokalnych wydarzeniach i aktywnościach',
        'Przydatne miejsca: usługi, sklepy, organizacje',
        'Widżety na ekranie głównym: pogoda, kursy walut, islandzkie święta, słowo dnia',
      ],
    },
    audience: {
      title: 'Dla kogo to jest',
      text: 'Niezależnie czy właśnie przeprowadziłeś się na Islandię, planujesz przeprowadzkę czy po prostu interesujesz się życiem na tej wyjątkowej wyspie - Umland jest tutaj, aby pomóc. Aplikacja jest zaprojektowana z myślą o ekspatach, ale jest otwarta dla wszystkich.',
    },
    languages: {
      title: 'Języki',
      text: 'Aplikacja jest dostępna w języku angielskim, rosyjskim i polskim. W przyszłości planujemy dodać więcej języków.',
    },
    contact: {
      title: 'Skontaktuj się z nami',
      text: 'Masz sugestie, znalazłeś błąd lub chcesz wnieść swój wkład? Chętnie usłyszymy od Ciebie!',
      email: 'support@umland.is',
    },
  },
};

export default function AboutUmlandPage() {
  const { language } = useAppLanguage();
  const t = content[language] || content.en;

  return (
    <DetailPageLayout>
      <DetailPageHeader title={t.title} />
      <div style={styles.content}>
        {/* Mission */}
        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>{t.mission.title}</h2>
          <p style={styles.bodyText}>{t.mission.text}</p>
        </section>

        {/* Features */}
        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>{t.features.title}</h2>
          <ul style={styles.list}>
            {t.features.items.map((item, index) => (
              <li key={index} style={styles.listItem}>{item}</li>
            ))}
          </ul>
        </section>

        {/* Audience */}
        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>{t.audience.title}</h2>
          <p style={styles.bodyText}>{t.audience.text}</p>
        </section>

        {/* Languages */}
        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>{t.languages.title}</h2>
          <p style={styles.bodyText}>{t.languages.text}</p>
        </section>

        {/* Contact */}
        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>{t.contact.title}</h2>
          <p style={styles.bodyText}>{t.contact.text}</p>
          <a href={`mailto:${t.contact.email}`} style={styles.emailLink}>
            {t.contact.email}
          </a>
        </section>
      </div>
    </DetailPageLayout>
  );
}

const styles = {
  content: {
    backgroundColor: Colors.background,
    minHeight: '100vh',
    padding: '24px',
  } as React.CSSProperties,

  section: {
    marginBottom: '28px',
  } as React.CSSProperties,

  sectionTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: Colors.text,
    marginBottom: '12px',
  } as React.CSSProperties,

  bodyText: {
    fontSize: '16px',
    color: Colors.text,
    lineHeight: '24px',
    margin: 0,
  } as React.CSSProperties,

  list: {
    margin: 0,
    paddingLeft: '20px',
  } as React.CSSProperties,

  listItem: {
    fontSize: '16px',
    color: Colors.text,
    lineHeight: '28px',
  } as React.CSSProperties,

  emailLink: {
    display: 'inline-block',
    marginTop: '8px',
    fontSize: '16px',
    color: Colors.primary,
    textDecoration: 'none',
    fontWeight: '500',
  } as React.CSSProperties,
};
